import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { type MouseEvent as ReactMouseEvent, useEffect } from 'react';
import { clearAtomFamily } from '@/utils/atomFamily';
import type { GeocodeLocation } from '@/views/Community/views/PartnerMap/components/LocationSearchBar/LocationAutocomplete';
import { DEFAULT_LOCALE_IDS } from '@/views/Community/views/PartnerMap/constants';
import type { MatchableAddress } from '@/views/Community/views/PartnerMap/types';
import {
  isStreetAddress,
  matches,
} from '@/views/Community/views/PartnerMap/utils/location';

export const selectedPartnerMapTeamId = atom<string | undefined>(undefined);
export const anchoredPartnerCardId = atom<string | undefined>(undefined);
export const teamDetailPanelVisible = atom(false);
export const viewAllTeamId = atom<string | undefined>(undefined);
export const activeViewAllGroup = atom<string | undefined>(undefined);

export const isActiveViewAllGroupFamily = atomFamily((groupKey: string) =>
  atom((get) => get(activeViewAllGroup) === groupKey),
);

export const isTeamDetailVisibleFamily = atomFamily((teamId: string) =>
  atom((get) => {
    const selectedTeamId = get(selectedPartnerMapTeamId);
    return get(teamDetailPanelVisible) && teamId === selectedTeamId;
  }),
);

export const isAnyTeamDetailVisibleFamily = atomFamily((teamIds: string[]) =>
  atom((get) => {
    const selectedTeamId = get(selectedPartnerMapTeamId);
    return (
      get(teamDetailPanelVisible) && teamIds.includes(selectedTeamId ?? '')
    );
  }),
);

export const selectedGeocodeLocation = atom<GeocodeLocation | null>(null);

export const selectedCommunityListingMapCommunityListingId = atom<
  string | undefined
>(undefined);

export const communityListingDetailPanelVisible = atom(false);

export const hoveredCommunityListingId = atom<string | undefined>(undefined);

export const anchoredCommunityListingsCardId = atom<string | undefined>(
  undefined,
);

export const mailingAddressMatchesSelectedLocationFamily = atomFamily(
  (mailingAddress: MatchableAddress | null | undefined) =>
    atom((get) => {
      const selectedLocation = get(selectedGeocodeLocation);
      if (
        !selectedLocation ||
        DEFAULT_LOCALE_IDS.includes(selectedLocation.id) ||
        !isStreetAddress(selectedLocation)
      ) {
        return false;
      }
      return matches(selectedLocation, mailingAddress);
    }),
);

export const selectedGroupKey = atom<string | undefined>(undefined);
export const isGroupSelectedFamily = atomFamily((groupKey: string) =>
  atom((get) => groupKey === get(selectedGroupKey)),
);

export const hoveredGroupKey = atom<string | undefined>(undefined);
export const isGroupHoveredFamily = atomFamily((groupKey: string) =>
  atom((get) => groupKey === get(hoveredGroupKey)),
);

export const hoveredTeamId = atom<string | undefined>(undefined);
export const isTeamHoveredFamily = atomFamily((teamId: string) =>
  atom((get) => get(hoveredTeamId) === teamId),
);

export const useIsAnyTeamHovered = () => {
  return !!useAtomValue(hoveredTeamId);
};

export const mapMarkers = atom<
  Record<string, google.maps.marker.AdvancedMarkerElement>
>({});
export const mapMarkerByKeyFamily = atomFamily((key: string) =>
  atom((get) => get(mapMarkers)[key]),
);

export const mapClickEventAtom = atom<ReactMouseEvent<
  Element,
  MouseEvent
> | null>(null);

export const useResetMapOnUnmount = () => {
  const setSelectedPartnerMapTeamId = useSetAtom(selectedPartnerMapTeamId);
  const setAnchoredPartnerCardId = useSetAtom(anchoredPartnerCardId);
  const setTeamDetailPanelVisible = useSetAtom(teamDetailPanelVisible);
  const setSelectedGeocodeLocation = useSetAtom(selectedGeocodeLocation);
  const setSelectedGroupKey = useSetAtom(selectedGroupKey);
  const setHoveredGroupKey = useSetAtom(hoveredGroupKey);
  const setHoveredTeamId = useSetAtom(hoveredTeamId);
  const setViewAllTeamId = useSetAtom(viewAllTeamId);
  const setActiveViewAllGroup = useSetAtom(activeViewAllGroup);
  const setMapMarkers = useSetAtom(mapMarkers);
  const setSelectedCommunityListingMapCommunityListingId = useSetAtom(
    selectedCommunityListingMapCommunityListingId,
  );
  const setCommunityListingDetailPanelVisible = useSetAtom(
    communityListingDetailPanelVisible,
  );
  const setHoveredCommunityListingId = useSetAtom(hoveredCommunityListingId);
  const setAnchoredCommunityListingsCardId = useSetAtom(
    anchoredCommunityListingsCardId,
  );

  useEffect(() => {
    return () => {
      setSelectedPartnerMapTeamId(undefined);
      setAnchoredPartnerCardId(undefined);
      setTeamDetailPanelVisible(false);
      setSelectedGeocodeLocation(null);
      setSelectedGroupKey(undefined);
      setHoveredGroupKey(undefined);
      setHoveredTeamId(undefined);
      setViewAllTeamId(undefined);
      setActiveViewAllGroup(undefined);
      setMapMarkers({});
      setSelectedCommunityListingMapCommunityListingId(undefined);
      setCommunityListingDetailPanelVisible(false);
      setHoveredCommunityListingId(undefined);
      setAnchoredCommunityListingsCardId(undefined);

      clearAtomFamily(isActiveViewAllGroupFamily);
      clearAtomFamily(isTeamDetailVisibleFamily);
      clearAtomFamily(isAnyTeamDetailVisibleFamily);
      clearAtomFamily(mailingAddressMatchesSelectedLocationFamily);
      clearAtomFamily(isCommunityListingDetailVisibleFamily);
      clearAtomFamily(isCommunityListingHoveredFamily);
      clearAtomFamily(isGroupSelectedFamily);
      clearAtomFamily(isGroupHoveredFamily);
      clearAtomFamily(isTeamHoveredFamily);
      clearAtomFamily(mapMarkerByKeyFamily);
    };
  }, [
    setSelectedPartnerMapTeamId,
    setAnchoredPartnerCardId,
    setTeamDetailPanelVisible,
    setSelectedGeocodeLocation,
    setSelectedGroupKey,
    setHoveredGroupKey,
    setHoveredTeamId,
    setViewAllTeamId,
    setActiveViewAllGroup,
    setMapMarkers,
    setSelectedCommunityListingMapCommunityListingId,
    setCommunityListingDetailPanelVisible,
    setHoveredCommunityListingId,
    setAnchoredCommunityListingsCardId,
  ]);
};

export const isCommunityListingDetailVisibleFamily = atomFamily((id: string) =>
  atom((get) => {
    const selectedListingId = get(
      selectedCommunityListingMapCommunityListingId,
    );
    return get(communityListingDetailPanelVisible) && id === selectedListingId;
  }),
);

export const isCommunityListingHoveredFamily = atomFamily((id: string) =>
  atom((get) => get(hoveredCommunityListingId) === id),
);
